import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = 'http://localhost:5000/api';

// Axios instance with credentials (sends cookies)
export const authAxios = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(true);

  // Attach access token to every request
  useEffect(() => {
    const id = authAxios.interceptors.request.use((config) => {
      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    });
    return () => authAxios.interceptors.request.eject(id);
  }, [accessToken]);

  // Auto-refresh access token using refresh cookie on mount
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await authAxios.post('/auth/refresh');
      setAccessToken(res.data.accessToken);
      return res.data.accessToken;
    } catch {
      setUser(null);
      setAccessToken('');
      return null;
    }
  }, []);

  // Fetch current user profile
  const fetchMe = useCallback(async (token) => {
    try {
      const res = await authAxios.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data.user);
    } catch {
      setUser(null);
    }
  }, []);

  // On mount — try to restore session via refresh token cookie
  useEffect(() => {
    const init = async () => {
      const token = await refreshAccessToken();
      if (token) await fetchMe(token);
      setLoading(false);
    };
    init();
  }, [refreshAccessToken, fetchMe]);

  // Google Sign-In
  const loginWithGoogle = async (credential) => {
    const res = await authAxios.post('/auth/google', { credential });
    setAccessToken(res.data.accessToken);
    setUser(res.data.user);
    return res.data.user;
  };

  // Email/Password Register
  const register = async ({ name, email, password, phone }) => {
    const res = await authAxios.post('/auth/register', { name, email, password, phone });
    setAccessToken(res.data.accessToken);
    setUser(res.data.user);
    return res.data.user;
  };

  // Email/Password Login
  const login = async ({ email, password }) => {
    const res = await authAxios.post('/auth/login', { email, password });
    setAccessToken(res.data.accessToken);
    setUser(res.data.user);
    return res.data.user;
  };

  // Logout
  const logout = async () => {
    try {
      await authAxios.post('/auth/logout');
    } catch {}
    setUser(null);
    setAccessToken('');
  };

  // Update profile
  const updateProfile = async (data) => {
    const res = await authAxios.patch('/auth/me', data);
    setUser(res.data.user);
    return res.data.user;
  };

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      loading,
      isLoggedIn: !!user,
      loginWithGoogle,
      register,
      login,
      logout,
      updateProfile,
      refreshAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
